import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import DateRangePickerDropdown from '../../../components/DateRangePickerDropdown';
import { RANGE_OPTIONS, getRangeLabel } from './controlCenterUtils';

export default function ControlCenterGlobalFilter({
  range,
  customDateStart,
  customDateEnd,
  onChange,
  isDarkMode,
}) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  // DateRangePickerDropdown can fire onStartChange + onEndChange synchronously back-to-back
  // (e.g. swapping an out-of-order pick, or restarting a range once both dates are set). A ref
  // (updated synchronously) avoids combining a fresh pick with a stale customDateStart/customDateEnd
  // prop from before the first call's state update has flowed back down.
  const pendingRef = React.useRef({ start: customDateStart, end: customDateEnd });
  const closeTimeoutRef = React.useRef(null);

  React.useEffect(() => {
    pendingRef.current = { start: customDateStart, end: customDateEnd };
  }, [customDateStart, customDateEnd]);

  const clearScheduledClose = React.useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  React.useEffect(() => clearScheduledClose, [clearScheduledClose]);

  const commitCustomRange = (start, end) => {
    let nextStart = start;
    let nextEnd = end;
    // Keep UI bounds ordered when both sides are set (start edited after end, etc.).
    if (nextStart && nextEnd) {
      const startMs = new Date(nextStart).getTime();
      const endMs = new Date(nextEnd).getTime();
      if (!Number.isNaN(startMs) && !Number.isNaN(endMs) && startMs > endMs) {
        const swapped = nextStart;
        nextStart = nextEnd;
        nextEnd = swapped;
      }
    }
    pendingRef.current = { start: nextStart, end: nextEnd };
    onChange({ range: 'custom', customDateStart: nextStart, customDateEnd: nextEnd });
  };

  const handleSelectPreset = (value) => {
    clearScheduledClose();
    pendingRef.current = { start: null, end: null };
    onChange({ range: value, customDateStart: null, customDateEnd: null });
    setAnchorEl(null);
  };

  const handleCustomStart = (date) => {
    commitCustomRange(date ? date.toISOString() : null, pendingRef.current.end);
  };

  const handleCustomEnd = (date) => {
    commitCustomRange(pendingRef.current.start, date ? date.toISOString() : null);
    // Delay the auto-close so a same-tick follow-up call (e.g. swapping an out-of-order pick)
    // has a chance to land. End-only or both ends are enough to apply (open-ended start allowed).
    clearScheduledClose();
    closeTimeoutRef.current = setTimeout(() => {
      if (pendingRef.current.end) {
        setAnchorEl(null);
      }
    }, 350);
  };

  const label = getRangeLabel(range, customDateStart, customDateEnd);

  return (
    <>
      <Button
        onClick={(e) => {
          clearScheduledClose();
          setAnchorEl(e.currentTarget);
        }}
        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 18 }} />}
        sx={{
          textTransform: 'none',
          color: '#8e8e93',
          fontSize: 13,
          fontWeight: 500,
          px: 1,
          minWidth: 'auto',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          clearScheduledClose();
          setAnchorEl(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { borderRadius: 2.5, mt: 0.5, width: 230 } } }}
      >
        <Box sx={{ p: 1 }}>
          <List dense disablePadding>
            {RANGE_OPTIONS.map((opt) => {
              const selected = range === opt.value;
              return (
                <ListItemButton
                  key={opt.value}
                  onClick={() => handleSelectPreset(opt.value)}
                  sx={{
                    borderRadius: 1.5,
                    mb: 0.5,
                    ...(selected && {
                      bgcolor: isDarkMode ? '#fff' : '#1a1a1a',
                      color: isDarkMode ? '#000' : '#fff',
                      '&:hover': { bgcolor: isDarkMode ? '#e0e0e0' : '#333' },
                    }),
                  }}
                >
                  <ListItemText primary={opt.label} primaryTypographyProps={{ fontSize: 13.5 }} />
                </ListItemButton>
              );
            })}
          </List>
          <Box sx={{ borderTop: '1px solid', borderColor: isDarkMode ? '#334155' : '#e7e7e7', pt: 1, mt: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <EventOutlinedIcon sx={{ fontSize: 14 }} />
              Custom range
            </Typography>
            <DateRangePickerDropdown
              label=""
              placeholder="Select date range"
              startDate={customDateStart ? new Date(customDateStart) : null}
              endDate={customDateEnd ? new Date(customDateEnd) : null}
              onStartChange={handleCustomStart}
              onEndChange={handleCustomEnd}
              isDarkMode={isDarkMode}
              sx={{ width: '100%', minWidth: 0 }}
            />
          </Box>
        </Box>
      </Popover>
    </>
  );
}
